/**
 * What each command prints when it actually finished.
 *
 * ## Why a marker is needed at all
 *
 * A run's terminal state comes from the ECS task's exit code, and installers
 * before MP-3615 **do not propagate Terraform's exit status** — they exit 0
 * after failing. From MP-3615 the image has an exit-code contract (below), but
 * `RunnerImage` is per account and per region, so nothing here can know every
 * account has moved, and the marker stays (IM-187).
 * Measured on apply `rmtvk9nhf-3r1plh` (2026-09-10): the log carried
 * `Error: Kubernetes cluster unreachable`, the installer said it could not
 * verify the deployment, and `describe-tasks` still reported
 * `containers[0].exitCode: 0`. The run was therefore recorded `succeeded`, and
 * because deployments derive from runs, the console showed a **live** deployment
 * whose environment URL returned 503. That is IM-80.
 *
 * The root fix belongs to the image (MP-3569). What this side can do is refuse
 * to take the exit code as the whole story, and the cheapest honest corroboration
 * is the line the command prints when it genuinely completed.
 *
 * ## Why these exact strings
 *
 * The installer's own, observed in the run logs of 2026-09-10 — not paraphrased,
 * for the same reason `DEPLOYMENT_OUTPUT_FIELDS` uses the real `printf` labels: a
 * marker this side invents matches nothing and reports every run as broken.
 *
 * `output` looks for a banner label rather than a completion line because the
 * banner *is* that command's product; if it printed, the command worked. The
 * label survives redaction — only the value beside it is masked — which is what
 * lets the runner wrapper match on it after the redactor has run.
 *
 * ## Why this lives here and not with the nightly
 *
 * Two things consume it and they are not peers: the nightly exercise
 * (`shared/src/nightly/`) uses it to decide whether a step genuinely passed, and
 * the runner wrapper (`shared/src/bootstrap/runner-wrapper.ts`) interpolates it
 * into the shell that runs in a customer's account. A deployment wrapper
 * importing from a test harness would be the wrong way round, and two copies
 * would be two answers to one question.
 */
export const COMPLETION_MARKERS = {
    plan: 'Terraform plan completed.',
    apply: 'Apply complete!',
    output: 'Environment URL:',
    destroy: 'Destroy complete!',
};
/**
 * The installer's exit codes that mean "Terraform applied, and then the
 * verification did not pass" (MP-3615, `docs/reference/exit-codes.md` in
 * `qrvey_mp_iac_deployment`).
 *
 * The rest of the contract needs no name here: 0 is success, 1 and 2 are
 * failures the run log explains, and 3 is this side's own refusal (IM-80). The
 * runner wrapper's own exit 2 — no state key, or a bucket the account does not
 * own — shares a number with the installer's config check, and both mean "fix
 * the config", so they are not told apart.
 *
 * These two are different from every other failure because **the
 * infrastructure changed**. Reading them as `installer-failed` reported the
 * previous chart version as the running one, refused `output` — which is the
 * contract's own recovery for 10 — and hid the endpoints the banner printed.
 */
export const INSTALLER_EXIT = {
    /** Applied, but the cluster was unreachable while the installer verified it. */
    appliedUnreachable: 10,
    /** Applied and reachable, but a verification point failed. */
    appliedUnverified: 11,
};
/** The failure reasons those two codes are recorded under. */
export const APPLIED_FAILURE_REASONS = ['applied-unreachable', 'applied-unverified'];
/**
 * Whether a run failed *after* Terraform applied — exit 10 or 11.
 *
 * On the run rather than on an exit code, because the exit code is not kept:
 * the completion handler turns it into a reason, and the reason is what every
 * reader downstream has. Structural, with a `string` state, so the console can
 * ask it of a run read off the wire without a cast.
 */
export const failedAfterApplying = (run) => run.state === 'failed' &&
    APPLIED_FAILURE_REASONS.includes(run.failure?.reason ?? '');
//# sourceMappingURL=completion.js.map